import { createSelector } from '@reduxjs/toolkit';
import { DataStatus, isError, isLoading, isFinished } from '@/shared/lib';
import type { AppThunk } from '@/shared/model';
import { createAppSlice } from '@/shared/model';
import * as SavedPresetsAPI from '../api/savedPresetsAPI';
import type { PresetViewType } from '../api/types';
import type { SavedPresetRecord } from './types';

interface State {
  presets: SavedPresetRecord[];
  status: DataStatus;
  requestedOrganizationId?: string;
}

const initialState: State = {
  presets: [],
  status: DataStatus.idle,
  requestedOrganizationId: undefined,
};

export const savedPresetsSlice = createAppSlice({
  name: 'savedPresets',
  initialState,
  reducers: (create) => ({
    setupPresets: create.asyncThunk<string, SavedPresetRecord[]>(
      () => {
        return SavedPresetsAPI.getSavedPresets();
      },
      {
        pending: (state, action) => {
          state.status = DataStatus.loading;
          state.requestedOrganizationId = action.meta.arg;
        },
        fulfilled: (state, action) => {
          state.presets = action.payload;
          state.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.status = DataStatus.error;
          state.presets = [];
          state.requestedOrganizationId = undefined;
        },
        options: {
          condition: (requestedOrganizationId, { getState }) => {
            const state = getState() as RootState;

            if (
              state.savedPresets.requestedOrganizationId ===
                requestedOrganizationId &&
              (isLoading(state.savedPresets.status) ||
                isFinished(state.savedPresets.status))
            ) {
              return false;
            }
          },
        },
      },
    ),
    createPreset: create.asyncThunk<
      {
        viewType: PresetViewType;
        name: string;
        preset: unknown;
        isDefault: boolean;
      },
      SavedPresetRecord
    >(
      ({ viewType, name, preset, isDefault }) => {
        return SavedPresetsAPI.createSavedPreset({
          viewType,
          name,
          preset,
          isDefault,
        });
      },
      {
        fulfilled: (state, action) => {
          if (action.payload.isDefault) {
            state.presets = state.presets.map((p) =>
              p.isDefault && p.viewType === action.payload.viewType
                ? { ...p, isDefault: false }
                : p,
            );
          }
          state.presets.unshift(action.payload);
        },
      },
    ),
    updatePreset: create.asyncThunk<
      {
        id: string;
        update: {
          name?: string;
          viewType?: PresetViewType;
          preset?: unknown;
          isDefault?: boolean;
        };
        hiddenUpdate?: boolean;
      },
      SavedPresetRecord
    >(
      ({ id, update }, { getState }) => {
        const state = getState() as RootState;
        const presets: SavedPresetRecord[] = selectPresets(state);
        const presetToUpdate = presets.find((preset) => {
          return preset.id === id;
        });

        if (!presetToUpdate) {
          throw new Error(`Update of unknown preset: ${id}`);
        }

        return SavedPresetsAPI.updateSavedPreset(id, update);
      },
      {
        pending: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg.id) {
              return {
                ...preset,
                updating: !action.meta.arg.hiddenUpdate,
              };
            }

            return preset;
          });
        },
        fulfilled: (state, action) => {
          if (action.payload.isDefault) {
            state.presets = state.presets.map((p) =>
              p.isDefault &&
              p.viewType === action.payload.viewType &&
              p.id !== action.payload.id
                ? { ...p, isDefault: false }
                : p,
            );
          }
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.payload.id) {
              return action.payload;
            }

            return preset;
          });
        },
      },
    ),
    deletePreset: create.asyncThunk(
      (id: string) => {
        return SavedPresetsAPI.deleteSavedPreset(id);
      },
      {
        pending: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return {
                ...preset,
                updating: true,
              };
            }

            return preset;
          });
        },
        fulfilled: (state, action) => {
          state.presets = state.presets.filter((preset) => {
            return preset.id !== action.meta.arg;
          });
        },
      },
    ),
  }),
  selectors: {
    selectPresetById: createSelector(
      (state: State) => state.presets,
      (_, presetId: string) => {
        return presetId;
      },
      (presets: SavedPresetRecord[], presetId: string) => {
        return presets.find((preset) => {
          return preset.id === presetId;
        });
      },
    ),
    selectPresets: (state) => {
      return state.presets;
    },
    selectIsLoading: (state) => {
      return isLoading(state.status);
    },
    selectIsFailed: (state) => {
      return isError(state.status);
    },
    selectIsFinished: (state) => {
      return isFinished(state.status);
    },
  },
});

export const { setupPresets, createPreset, deletePreset, updatePreset } =
  savedPresetsSlice.actions;
export const {
  selectIsFailed,
  selectIsLoading,
  selectPresets,
  selectIsFinished,
  selectPresetById,
} = savedPresetsSlice.selectors;

export const unsetAsDefault =
  (id: string, hiddenUpdate?: boolean): AppThunk<Promise<void>> =>
  async (dispatch) => {
    await dispatch(
      updatePreset({
        id,
        update: { isDefault: false },
        hiddenUpdate,
      }),
    );
  };

export const setAsDefault =
  (id: string): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const presets: SavedPresetRecord[] = selectPresets(state);
    const targetPreset = presets.find((preset) => preset.id === id);
    const existingDefault = presets.find((preset) => {
      return preset.isDefault && preset.viewType === targetPreset?.viewType;
    });

    await Promise.all([
      existingDefault
        ? dispatch(unsetAsDefault(existingDefault.id, true))
        : Promise.resolve(),
      dispatch(updatePreset({ id, update: { isDefault: true } })),
    ]);
  };
